import React from 'react';
import {
  Form,
  Input,
  Button,
  Divider,
  Select,
  PageHeader,
  Switch,
  InputNumber,
  Alert,
} from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import {
  addAppTagDefinition,
  updateAppTagDefinition,
} from 'src/features/appTagDefinitions/appTagDefinitionsSlice';
import merge from 'deepmerge';
import isPlainObject from 'is-plain-object';
import { useFormik } from 'formik';
import { transformFiles, overwriteMerge } from 'src/helpers';
import { snakeCase } from 'lodash';
/** type imports */
import type { Store } from 'rc-field-form/es/interface';
import type { AppDispatch } from 'src/app/store';
import {
  fetchFileReferences,
} from 'src/features/storage/storageSlice';
import type { RootState } from 'src/app/rootReducer';
import type {
  FormItemProps,
  AppTagDefinition,
  AppTagFormState,
} from 'types';

interface Props {
  appTagFormState: AppTagFormState;
  onCancel: () => void;
  editMode: boolean;
}

const AppTagDefinitionFormContainer: React.FC<Props> = ({ appTagFormState, onCancel, editMode }: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    fileReferences: {
      imageFiles,
      videoFiles,
      audioFiles,
    },
    fetchingFileReferences,
  } = useSelector((state: RootState) => state.storageState);

  const imageOptions = transformFiles(imageFiles);
  // const videoOptions = transformFiles(videoFiles);
  const audioOptions = transformFiles(audioFiles);

  const audioPlaceholder = 'audio/example.mp3';
  // const videoPlaceholder = 'video/example.mp4';
  const imagePlaceholder = 'image/example.jpeg';

  React.useEffect(() => {
   // dispatch(fetchCharacterProfiles());
    dispatch(fetchFileReferences());
  }, [dispatch]);

  const [antdForm] = Form.useForm();

  // const {
  //   fileReferences: {
  //     imageFiles,
  //     // videoFiles,
  //     // audioFiles,
  //   },
  //   fetchingFileReferences,
  // } = useSelector((state: RootState) => state.storageState);

  const formik = useFormik<AppTagFormState>({
    initialValues: appTagFormState,
    enableReinitialize: true,
    onSubmit: async (values, formikHelpers): Promise<void> => {
      const { id, appTagDefinition } = values;
      const { setSubmitting, resetForm, setErrors } = formikHelpers;
      let success, validationError;
      try {
        if (editMode) {
          ({ success, validationError } = await dispatch(updateAppTagDefinition(id, appTagDefinition)));
        } else {
          ({ success, validationError } = await dispatch(addAppTagDefinition(id, appTagDefinition)));
        }
        setSubmitting(false);
        if (success) {
          if (!editMode) {
            resetForm();
            antdForm.resetFields();
          }
        } else if (validationError) {
          console.log(validationError);
          const { path, message } = validationError;
          setErrors({
            [path]: message,
          });
        }
      } catch (error) {
        console.log('Non validation error:', error);
      }
    },
  });

  const {
    values: formikValues,
    errors,
    // handleChange,
    // handleBlur,
    handleSubmit,
    isSubmitting,
    // setFieldValue,
    // setSubmitting,
    // setErrors,
    /* and other goodies */
    initialValues: formikInitialValues,
    setValues,
    dirty,
  } = formik;

  React.useEffect(() => {
    antdForm.resetFields();
  }, [antdForm, formikInitialValues]);

  React.useEffect(() => {
    dispatch(fetchFileReferences());
  }, [dispatch]);

  // const imageOptions = transformFiles(imageFiles);
  // const imagePlaceholder = 'image/example.jpeg';

  function handleValuesChange(changedValues: Store /*, currentValues: Store */): void {
    const newValues = merge<AppTagFormState, Store>(formikValues, changedValues, {
      arrayMerge: overwriteMerge,
      isMergeableObject: isPlainObject,
    });

    if (!editMode) {
      const { appTagDefinition: { displayName } } = newValues;
      newValues.id = snakeCase(displayName);
    }
    setValues(newValues);
  }


  type FormErrors = {
    [K in keyof AppTagDefinition]?: string;
  }

  function generateFormItemProps(itemName: keyof AppTagDefinition): FormItemProps {
    const hasError = itemName in errors;
    return {
      name: ['appTagDefinition', itemName],
      validateStatus: hasError ? 'error' : '',
      hasFeedback: hasError,
      help: hasError ? (errors as FormErrors)[itemName] : false,
    };
  }

  const { id, appTagDefinition } = formikValues;
  const {
    displayName,
    marketingTitle,
    marketingDescription,
    bannerImage,
    iconImage,
    appStorePageLink,
    rank,
  } = appTagDefinition;

  const questionDefStyle = {};
  // if (isDeleted) {
  //   questionDefStyle = {
  //     textDecorationLine: 'line-through',
  //     textDecorationStyle: 'solid',
  //   };
  // }

  // async function onDelete(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> {
  //   event.preventDefault();
  //   try {
  //     setSubmitting(true);
  //     // await dispatch(deleteNodeDefinition(id));
  //     console.log('deleted');
  //   } catch (error) {
  //     console.log('error:', error);
  //   }
  //   setSubmitting(false);
  // }
  const BottomButtons: React.FC = () => {
    if (editMode) {
      // let deleteButtonContent = 'Archive Story';
      // if (status === 'archived') {
      //   deleteButtonContent = 'Re-Publish Story';
      // }
      let updateButtonContent = 'Update app tag definition';
      if (!dirty) {
        updateButtonContent = 'Make a change to update';
      }
      return (
        <Form.Item>
          <Button
            block
            size={'large'}
            type={'primary'}
            htmlType={'submit'}
            loading={isSubmitting}
            disabled={isSubmitting || !dirty}
          >
            {updateButtonContent}
          </Button>
          {/* <Button
            block
            danger
            onClick={onDelete}
            size={'large'}
            type={'primary'}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {deleteButtonContent}
          </Button> */}
        </Form.Item>
      );
    } else {
      return (
        <Form.Item>
          <Button
            block
            size={'large'}
            type={'primary'}
            htmlType={'submit'}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Create new app tag definition
          </Button>
        </Form.Item>
      );
    }
  };
  
  return (
    <React.Fragment>
      <Form
        size={'middle'}
        layout={'vertical'}
        initialValues={formikInitialValues}
        form={antdForm}
        onValuesChange={handleValuesChange}
        onSubmitCapture={handleSubmit}
      >
        <PageHeader
          title={<Form.Item name={'id'}><span style={questionDefStyle}>ID: {id}</span></Form.Item>}
          extra={[
            <Button
              key={'close_sidebar'}
              danger
              icon={<CloseOutlined />}
              onClick={onCancel}
            />
          ]}
        >
          <Form.Item
            {...generateFormItemProps('displayName')}
            label={'Display Name'}
          >
            <Input
              placeholder={'Biology'}
              disabled={isSubmitting}
              value={displayName}
            />
          </Form.Item>
          <Form.Item
            {...generateFormItemProps('marketingTitle')}
            label={'App Marketing Title'}
          >
            <Input
              placeholder={'App for Kids'}
              disabled={isSubmitting}
              value={marketingTitle}
            />
          </Form.Item>
          
          <Form.Item
            {...generateFormItemProps('marketingDescription')}
            label={'App Marketing Description'}
          >
            <Input
              placeholder={'Fun facts all in an app'}
              disabled={isSubmitting}
              value={marketingDescription}
            />
          </Form.Item>

          <Form.Item
            {...generateFormItemProps('appStorePageLink')}
            label={'App Store Page Link'}
          >
            <Input
              placeholder={'http://'}
              disabled={isSubmitting}
              value={appStorePageLink}
            />
          </Form.Item>

          <Form.Item
            {...generateFormItemProps('bannerImage')}
            label={'Banner Image File'}
          >
            <Select
              allowClear
              showSearch
              options={imageOptions}
              disabled={isSubmitting}
              loading={fetchingFileReferences}
              value={bannerImage}
              placeholder={imagePlaceholder}
            />
          </Form.Item>

          <Form.Item
            {...generateFormItemProps('iconImage')}
            label={'App Icon Image File'}
          >
            <Select
              allowClear
              showSearch
              options={imageOptions}
              disabled={isSubmitting}
              loading={fetchingFileReferences}
              value={iconImage}
              placeholder={imagePlaceholder}
            />
          </Form.Item>

          <Form.Item
            {...generateFormItemProps('rank')}
            label={'Rank order (1 is first)'}
          >
            <InputNumber
              disabled={isSubmitting}
              value={rank}
            />
          </Form.Item>


          <Divider />
          {Object.values(errors).map((error, index) => <Alert style={{ marginBottom: 10 }} showIcon type={'error'} key={index} message={error} />)}
          <BottomButtons />
        </PageHeader>
      </Form>
    </React.Fragment>
  );
};

export default AppTagDefinitionFormContainer;